import type { QueryClient, QueryKey } from '@tanstack/react-query'

/** 一覧キャッシュ上の最小形（id で対象行を特定し、状態と表示順用の updatedAt を更新する）。 */
interface ListCustomerRow {
  id: string
  [key: string]: unknown
}

/**
 * ステータス変更の楽観的更新を「一覧キャッシュ」へ当てる。
 *
 * `use-customers.ts` の `useUpdateCustomerStatus` から切り出した（2026-08-12）。
 * 同フックへ onSuccess（サーバ応答の updatedAt を detail へ merge）を足したところ
 * `max-lines-per-function`(80行) とファイルの max-lines ラチェットに同時に触れたため、
 * disable ではなく責務を分離した（教訓 6-74）。**挙動は切り出し前と同一。**
 *
 * 一覧はカードが消えるのを防ぐため即時反映する。detail と違い `updatedAt` をフロント時刻で
 * 進めるが、これは**一覧の並び替え用**であって楽観ロックの token には使わない
 * （token は常に detail cache か明示指定から取る）。
 *
 * `customerKeys` は use-customers.ts 側の定義なので、循環 import を避けて
 * キーを引数で受け取る。
 *
 * @returns ロールバック用の [queryKey, 変更前データ] の配列
 */
export function applyStatusToListCaches(
  queryClient: QueryClient,
  listsKey: QueryKey,
  id: string,
  status: string
): [unknown, unknown][] {
  const previousLists: [unknown, unknown][] = []
  const patch = (c: ListCustomerRow) =>
    c.id === id ? { ...c, pipelineStatus: status, updatedAt: new Date().toISOString() } : c

  queryClient.getQueriesData({ queryKey: listsKey }).forEach(([key, data]) => {
    if (!data || typeof data !== 'object') return
    previousLists.push([key, data])

    const record = data as Record<string, unknown>
    if (Array.isArray(record.data)) {
      // PaginatedResult 形式: { data: [...], total, page, ... }
      queryClient.setQueryData(key, {
        ...record,
        data: (record.data as ListCustomerRow[]).map(patch),
      })
    } else if (Array.isArray(data)) {
      // 単純配列形式
      queryClient.setQueryData(key, (data as ListCustomerRow[]).map(patch))
    }
  })

  return previousLists
}
